import { z } from 'zod';
import { cursorPageSchema, cursorQuerySchema } from './pagination.js';
import { e164Schema } from '../phone.js';
/**
 * Домен переписки.
 *
 * Главное свойство этих типов — они ничего не знают о WhatsApp, ChatApp
 * и Meta. Канал описан парой «провайдер + его идентификаторы», сообщение —
 * направлением, автором и телом. Это не абстракция ради абстракции: на
 * первом этапе агент собирается и проверяется на внутреннем тест-канале
 * внутри админки, без единого внешнего вызова, и работает он тем же кодом,
 * которым потом пойдёт живая переписка. Если бы домен знал про WhatsApp,
 * такой проверки не существовало бы.
 */
// ─── каналы ───
export const CHANNEL_PROVIDERS = ['internal', 'chatapp'];
export const channelProviderSchema = z.enum(CHANNEL_PROVIDERS);
export const channelStatusSchema = z.enum(['active', 'disabled']);
/**
 * Что канал умеет.
 *
 * Спрашивается у провайдера, а не задаётся в настройках, и проверяется до
 * отправки. Без этого система узнавала бы об отсутствии шаблонов из ошибки
 * внешнего API — то есть после того, как сообщение не ушло клиенту.
 */
export const channelCapabilitiesSchema = z.object({
    /** Свободный текст. У внутреннего канала есть, у QR-WhatsApp тоже. */
    text: z.boolean(),
    /** Шаблоны Meta. Нужны, чтобы написать первым вне окна 24 часов. */
    templates: z.boolean(),
    /** Отдельный эндпоинт Meta для маркетинговых шаблонов. */
    marketingTemplates: z.boolean(),
    /** Файлы и картинки. */
    media: z.boolean(),
    /** Максимальная длина одного сообщения. Порог разбивки берётся отсюда. */
    maxTextLength: z.number().int().positive(),
    /** Есть ли окно обслуживания 24 часа. У внутреннего канала нет. */
    serviceWindow: z.boolean(),
});
export const channelSchema = z.object({
    id: z.uuid(),
    provider: channelProviderSchema,
    name: z.string(),
    /** Идентификатор линии у провайдера. У внутреннего канала пусто. */
    licenseId: z.string().nullable(),
    messengerType: z.string().nullable(),
    phoneE164: z.string().nullable(),
    status: channelStatusSchema,
    /** Канал на паузе: сообщения принимаются и сохраняются, агент не отвечает. */
    isPaused: z.boolean(),
    capabilities: channelCapabilitiesSchema,
    /** Кто отвечает в канале. null — агент не назначен, бот молчит. */
    agentId: z.uuid().nullable(),
    agentName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const createInternalChannelInputSchema = z.object({
    name: z.string().min(1).max(100).default('Тест-канал'),
});
/**
 * Подключение линии ChatApp.
 *
 * Все три значения человек переписывает из кабинета ChatApp, и ни одно
 * из них мы вывести не можем: номер лицензии и тип мессенджера — их
 * внутренние обозначения, телефон — то, что физически привязано к линии.
 * Поэтому поля обязательные: линия без них не отправит ничего, а
 * обнаружится это на первом живом клиенте.
 */
export const createChatAppChannelInputSchema = z.object({
    name: z.string().min(1).max(100),
    /** licenseId из кабинета ChatApp. Число, но храним строкой — это код. */
    licenseId: z.string().trim().min(1).max(50),
    /**
     * Тип мессенджера у ChatApp. `WhatsApp` — официальная линия Cloud API,
     * `grWhatsApp` — неофициальная. От этого зависит предел длины
     * сообщения, поэтому значение выбирается, а не пишется свободно.
     */
    messengerType: z.enum(['WhatsApp', 'grWhatsApp', 'caWhatsApp']),
    /** Номер линии в международном виде — для человека, не для отправки. */
    phoneE164: z.string().trim().min(5).max(20),
});
/**
 * Адрес вебхука одной линии.
 *
 * Отдаётся отдельным запросом, а не в общем списке каналов: в адресе
 * секрет, и попадать он должен на один экран подключения, а не в каждый
 * ответ, который кто-нибудь когда-нибудь запишет в журнал.
 */
export const channelWebhookSchema = z.object({
    /** Полный адрес, который вставляется в ChatApp. */
    url: z.string(),
    /** Настроен ли публичный адрес системы. Без него адрес собрать не из чего. */
    configured: z.boolean(),
    /** События, на которые подписываемся. */
    events: z.array(z.string()),
});
export const updateChannelInputSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    isPaused: z.boolean().optional(),
    status: channelStatusSchema.optional(),
    /**
     * Агент, отвечающий в этом канале. `null` — отвязать.
     *
     * Отдельно от остальных полей стоит `nullable`: «не передали» и
     * «передали пустоту» здесь означают разное — не трогать привязку
     * и снять её. Без этого различия отвязать агента было бы нечем.
     */
    agentId: z.uuid().nullable().optional(),
});
// ─── контакты ───
export const consentStateSchema = z.enum(['unknown', 'granted', 'revoked']);
/**
 * Откуда взялось имя контакта, от худшего источника к лучшему.
 *
 * `profile` — имя из профиля WhatsApp. Бесплатное и почти всегда
 * ненадёжное: в LATAM это сплошь и рядом прозвище, эмодзи или название
 * магазина. Уступает любому другому источнику.
 *
 * `client` — человек представился в переписке. Лучше профиля, но остаётся
 * извлечением из текста и потому уступает тому, что ввели люди.
 *
 * `lead` — имя пришло из данных лида: выгрузка, кампания, внешний учёт.
 *
 * `operator` — вписано руками. Не уступает никому: человек, поправивший
 * имя в карточке, сделал это, глядя на диалог.
 */
export const nameSourceSchema = z.enum(['profile', 'client', 'lead', 'operator']);
/**
 * Право на перезапись: имя заменяется, только если новый источник не хуже
 * записанного. Числа нужны для сравнения и наружу не выходят — и в API,
 * и в базе живут имена источников, а не ранги.
 */
export const NAME_SOURCE_RANK = {
    profile: 1,
    client: 2,
    lead: 3,
    operator: 4,
};
/**
 * Исход диалога — одна из трёх независимых осей, а не «статус вообще».
 *
 * Здесь только то, чем закончился **разговор**. Что стало с заказом и
 * ушла ли конверсия в трекер — отдельные поля и отдельные жизни. В старой
 * системе эти три вещи были смешаны в одно поле, и на вопрос «заказ
 * подтверждён, а конверсия ушла?» ответить было нельзя.
 *
 * `handedToCallCenter` — клиент согласился на звонок, и его передали
 * колл-центру. Это не догадка «клиент выглядит готовым», а зафиксированное
 * согласие: так это работало в старой системе, и так проверяемо.
 */
export const CONVERSATION_OUTCOMES = ['handedToCallCenter', 'ordered', 'refused'];
export const conversationOutcomeSchema = z.enum(CONVERSATION_OUTCOMES);
export const CONVERSATION_OUTCOME_LABELS = {
    handedToCallCenter: 'Передан в колл-центр',
    ordered: 'Оформил заказ',
    refused: 'Отказался',
};
/** Кто поставил исход. Пустой источник невозможен: ставит либо человек, либо агент. */
export const outcomeSourceSchema = z.enum(['operator', 'agent']);
export const contactSchema = z.object({
    id: z.uuid(),
    phoneE164: z.string(),
    name: z.string().nullable(),
    nameSource: nameSourceSchema.nullable(),
    /** Как обращаться, если это не совпадает с именем. Пусто — по имени. */
    addressAs: z.string().nullable(),
    language: z.string().nullable(),
    country: z.string().nullable(),
    isBlacklisted: z.boolean(),
    consentState: consentStateSchema,
    createdAt: z.iso.datetime(),
});
// ─── сообщения ───
export const messageDirectionSchema = z.enum(['inbound', 'outbound']);
/**
 * Кто автор сообщения.
 *
 * Четыре значения, и разница между `ai` и `operator` нужна не для
 * статистики: оператор, вмешавшийся в диалог, ставит его на паузу, а его
 * реплики при этом обязаны попасть в контекст агента — иначе после снятия
 * паузы агент начнёт разговор заново. Разметка автора и есть то, что
 * позволяет отличить одно от другого.
 */
export const messageSenderSchema = z.enum(['customer', 'ai', 'operator', 'system']);
export const messageTypeSchema = z.enum(['text', 'image', 'video', 'document', 'audio', 'template']);
/**
 * Состояние доставки. Словарь дословно совпадает с ChatApp — `sent`,
 * `delivered`, `viewed`, `failed` — плюс наши `pending` для «ещё не
 * отправлено» и `received` для входящих. Совпадение намеренное: чужие
 * статусы не переводятся в свои, чтобы при разборе не приходилось держать
 * в голове таблицу соответствий.
 */
export const messageStatusSchema = z.enum([
    'pending',
    'sent',
    'delivered',
    'viewed',
    'failed',
    'received',
]);
export const messageSchema = z.object({
    id: z.uuid(),
    conversationId: z.uuid(),
    direction: messageDirectionSchema,
    sender: messageSenderSchema,
    type: messageTypeSchema,
    body: z.string().nullable(),
    attachments: z.array(z.object({ url: z.string(), name: z.string().nullable() })).nullable(),
    providerMessageId: z.string().nullable(),
    /** Наш идентификатор, который провайдер возвращает в событиях статуса. */
    trackingKey: z.string().nullable(),
    status: messageStatusSchema,
    errorCode: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
// ─── диалоги ───
export const conversationStateSchema = z.enum(['open', 'closed']);
export const conversationSchema = z.object({
    id: z.uuid(),
    channelId: z.uuid(),
    channelName: z.string(),
    channelProvider: channelProviderSchema,
    contact: contactSchema,
    state: conversationStateSchema,
    isPaused: z.boolean(),
    pausedByEmail: z.string().nullable(),
    pausedAt: z.iso.datetime().nullable(),
    /**
     * Диалог ждёт живого человека.
     *
     * Отдельный признак, а не «пауза»: пауза означает «агент молчит», и
     * поставить её можно по десятку причин — проверяем гипотезу, клиент
     * попросил перезвонить, идёт разбор. «Ждёт оператора» означает «здесь
     * нужен человек, и пока он не придёт, ничего не случится». Если бы это
     * было одно поле, очередь на разбор смешалась бы с диалогами, которые
     * просто поставили на паузу, и очередь перестала бы работать.
     *
     * Ставится сейчас вручную; на шаге 3.2 её начнёт ставить сам агент
     * инструментом `handoff_to_human`, и поле для этого уже готово.
     */
    needsOperator: z.boolean(),
    /** Почему нужен человек. Пусто — причину не указали. */
    handoffReason: z.string().nullable(),
    handoffAt: z.iso.datetime().nullable(),
    handoffByEmail: z.string().nullable(),
    /**
     * Чем закончился разговор. Пусто — ещё идёт.
     *
     * Отдельно от `state`: `state` отвечает на вопрос «принимаем ли мы сюда
     * новые сообщения», исход — на вопрос «чем дело кончилось». Диалог
     * можно закрыть без исхода (человек просто перестал отвечать), и можно
     * зафиксировать исход, не закрывая диалог.
     */
    outcome: conversationOutcomeSchema.nullable(),
    outcomeSource: outcomeSourceSchema.nullable(),
    outcomeAt: z.iso.datetime().nullable(),
    outcomeByEmail: z.string().nullable(),
    /** Чем обосновали исход. Для передачи в колл-центр — что именно сказал клиент. */
    outcomeNote: z.string().nullable(),
    /**
     * Что разобрано из первого сообщения клиента: товар, источник трафика
     * и группа оффера. Оператору это отвечает на вопрос «о чём вообще
     * разговор», а `subid` — единственное, чем конверсия привязывается
     * к кампании.
     */
    productName: z.string().nullable(),
    subid: z.string().nullable(),
    offerGroupId: z.string().nullable(),
    /** Когда закрывается окно обслуживания. Пусто — окна у канала нет. */
    serviceWindowExpiresAt: z.iso.datetime().nullable(),
    lastInboundAt: z.iso.datetime().nullable(),
    lastBotMessageAt: z.iso.datetime().nullable(),
    firstHumanReplyAt: z.iso.datetime().nullable(),
    messageCount: z.number().int().nonnegative(),
    /** Последнее сообщение — для списка диалогов. */
    lastMessage: z
        .object({
        body: z.string().nullable(),
        sender: messageSenderSchema,
        createdAt: z.iso.datetime(),
    })
        .nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const conversationQuerySchema = cursorQuerySchema.extend({
    channelId: z.uuid().optional(),
    state: conversationStateSchema.optional(),
    /** Только диалоги на паузе: агент в них молчит. */
    pausedOnly: z.coerce.boolean().optional(),
    /** Только те, где ждут живого человека. Это рабочая очередь оператора. */
    needsOperator: z.coerce.boolean().optional(),
    /** Отбор по исходу разговора. */
    outcome: conversationOutcomeSchema.optional(),
    /** Поиск по телефону или имени контакта. */
    search: z.string().max(100).optional(),
});
export const conversationPageSchema = cursorPageSchema(conversationSchema);
export const messageQuerySchema = cursorQuerySchema;
export const messagePageSchema = cursorPageSchema(messageSchema);
// ─── подача входящего сообщения во внутренний канал ───
/**
 * Вход тест-канала. Это то, чем на первом этапе заменяется вебхук ChatApp:
 * оператор подаёт сообщение «от клиента» из админки, и дальше оно идёт
 * ровно тем же путём — дедупликация, маршрутизация, гейты, очередь.
 */
export const simulateInboundInputSchema = z.object({
    phone: z.string().min(3).max(30),
    /** Страна для номеров без кода. Берётся из настроек, а не угадывается. */
    defaultCountry: z.string().length(2).optional(),
    text: z.string().min(1).max(4096),
    name: z.string().max(200).optional(),
    /**
     * Идентификатор сообщения у провайдера. Если задан, повторная подача
     * с тем же значением не создаёт второго сообщения — так проверяется
     * защита от повторной доставки вебхука.
     */
    providerMessageId: z.string().min(1).max(200).optional(),
});
export const inboundResultSchema = z.object({
    conversationId: z.uuid(),
    messageId: z.uuid(),
    contactId: z.uuid(),
    /** Создано новое сообщение или это был повтор уже принятого. */
    duplicate: z.boolean(),
    /** Поставлена ли задача на обработку агентом и почему нет. */
    queued: z.boolean(),
    skipReason: z.string().nullable(),
});
/** Ручной ответ оператора. */
export const sendOperatorMessageInputSchema = z.object({
    text: z.string().min(1).max(4096),
    /**
     * Поставить диалог на паузу, если он ещё не на паузе.
     *
     * По умолчанию да, и это не послабление правила, а само правило:
     * оператор, который пишет клиенту, уже вмешался в диалог, и агент
     * обязан замолчать — иначе клиент получит две реплики на одно своё
     * сообщение. Раньше сервер в таком случае отказывал и просил нажать
     * паузу отдельно; отказ остался для вызовов с `false`, когда вызывающий
     * хочет отправить только в уже приостановленный диалог.
     */
    autoPause: z.boolean().default(true),
});
export const updateConversationInputSchema = z.object({
    isPaused: z.boolean().optional(),
    state: conversationStateSchema.optional(),
});
/** Пометить диалог как ожидающий человека или снять пометку. */
export const setHandoffInputSchema = z.object({
    needsOperator: z.boolean(),
    reason: z.string().trim().max(300).default(''),
});
/**
 * Зафиксировать исход разговора или снять его.
 *
 * `outcome: null` снимает исход — это не «отмена события», а исправление
 * ошибки, и в журнал оно попадает так же, как постановка.
 */
export const setOutcomeInputSchema = z.object({
    outcome: conversationOutcomeSchema.nullable(),
    note: z.string().trim().max(300).default(''),
});
/** Контакт, каким его создаёт или правит оператор. */
export const upsertContactInputSchema = z.object({
    phone: z.string().min(3).max(30),
    defaultCountry: z.string().length(2).optional(),
    name: z.string().max(200).optional(),
    /**
     * Источник имени. Не выводится из того, кто вызвал метод: тот же метод
     * обслуживает и ввод оператора, и загрузку лидов, и приём входящего.
     * Значение по умолчанию не задаётся намеренно — вызывающий обязан
     * сказать, откуда имя, иначе «откуда» перестанет что-либо значить.
     */
    nameSource: nameSourceSchema.optional(),
    addressAs: z.string().max(200).optional(),
    language: z.string().max(10).optional(),
});
export const setBlacklistInputSchema = z.object({ isBlacklisted: z.boolean() });
/** Схема телефона для мест, где он уже нормализован. */
export { e164Schema };
//# sourceMappingURL=messaging.js.map